// src/pages/admin/media/ImageList.tsx
import React, { useEffect, useState } from 'react';
import { useObjectsApi } from '../../../api/objects/objects';
import { TImage } from '../../../models/objects/media';
import { Table, Button, Space, message, Input, Spin, Select } from 'antd';
import { UploadOutlined, EditOutlined, SaveOutlined, StopOutlined, DeleteOutlined } from '@ant-design/icons';
import { getImagePath } from '../../../utils/format/valueFormat';
import { useTranslation } from 'react-i18next';

const ImageList: React.FC = () => {
    const { t } = useTranslation();
    const { getImages, createImage, updateImage, deleteImage, uploadImage } = useObjectsApi();
    const [images, setImages] = useState<TImage[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [editingKey, setEditingKey] = useState<number | null>(null);
    const [editingImage, setEditingImage] = useState<TImage | null>(null);
    const [loadingRow, setLoadingRow] = useState<number | null>(null);
    const [addingId, setAddingId] = useState<number>(0);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [filterCategory, setFilterCategory] = useState<'all' | 'project' | 'general'>('all');

    useEffect(() => {
        setLoading(true);
        getImages(filterCategory === 'all' ? undefined : filterCategory)
            .then(resp => {
                // ← Assure-toi que resp.data est toujours un tableau
                const imgs = Array.isArray(resp.data) ? resp.data : [];
                setImages(imgs);
            })
            .catch(err => message.error(err.response?.data || err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [filterCategory]);

    const handleAdd = () => {
        const newId = addingId - 1;
        const newImage: TImage = {
            id: newId,
            file_name: '',
            file_path: '',
            category: filterCategory === 'all' ? 'general' : filterCategory,
        };
        setAddingId(newId);
        setEditingKey(newId);
        setEditingImage(newImage);

        // ← Utilise la version fonctionnelle et protège contre un état non itérable
        setImages(prev => Array.isArray(prev) ? [newImage, ...prev] : [newImage]);
    };

    const edit = (record: TImage) => {
        setEditingKey(record.id);
        setEditingImage({ ...record });
    };

    const cancel = () => {
        if (editingImage && editingImage.id < 0) {
            setImages(images.filter(img => img.id !== editingImage.id));
        }
        setEditingKey(null);
        setEditingImage(null);
    };

    const save = (id: number) => {
        if (!editingImage) return;
        setLoadingRow(id);
        const action = editingImage.id > 0
            ? updateImage(editingImage.id, editingImage)
            : createImage(editingImage);
        action
            .then(resp => {
                const updatedImage = resp.data;
                setImages(images.map(img => img.id === id ? updatedImage : img));
                message.success(t('message.savedSuccessfully'));
            })
            .catch(err => {
                message.error(err.response?.data || err.message);
            })
            .finally(() => {
                setLoadingRow(null);
                setEditingKey(null);
                setEditingImage(null);
            });
    };

    const handleDelete = (id: number) => {
        setDeleteLoading(true);
        deleteImage(id)
            .then(() => {
                setImages(images.filter(img => img.id !== id));
                message.success(t('message.deletedSuccessfully'));
            })
            .catch(err => {
                message.error(err.response?.data || err.message);
            })
            .finally(() => setDeleteLoading(false));
    };

    const uploadFile = (record: TImage) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e: any) => {
            const file = e.target.files[0];
            if (file) {
                setLoadingRow(record.id);
                const formData = new FormData();
                formData.append('image', file);
                uploadImage(record.id, formData)
                    .then(resp => {
                        const updatedImage = resp.data;
                        setImages(images.map(img => img.id === record.id ? updatedImage : img));
                        message.success(t('message.savedSuccessfully'));
                    })
                    .catch(err => {
                        message.error(err.response?.data || err.message);
                    })
                    .finally(() => setLoadingRow(null));
            }
        };
        input.click();
    };

    const columns = [
        {
            title: t('media.tableHeaders.id'),
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: t('media.tableHeaders.fileName'),
            dataIndex: 'file_name',
            key: 'file_name',
            render: (text: string, record: TImage) =>
                editingKey === record.id ? (
                    <Input
                        value={editingImage?.file_name}
                        onChange={(e) =>
                            setEditingImage(editingImage ? { ...editingImage, file_name: e.target.value } : null)
                        }
                    />
                ) : (
                    text
                ),
        },
        {
            title: t('media.tableHeaders.category'),
            dataIndex: 'category',
            key: 'category',
            render: (_: string, record: TImage) =>
                editingKey === record.id ? (
                    <Select
                        value={editingImage?.category || 'general'}
                        onChange={(val) =>
                            setEditingImage(editingImage ? { ...editingImage, category: val } : null)
                        }
                        style={{ width: 160 }}
                        options={[
                            { value: 'project', label: t('media.categories.project') },
                            { value: 'general', label: t('media.categories.general') },
                        ]}
                    />
                ) : (
                    t(
                        record.category === 'project'
                            ? 'media.categories.project'
                            : 'media.categories.general'
                    )
                ),
        },
        {
            title: t('media.tableHeaders.thumbnail'),
            dataIndex: 'file_path',
            key: 'thumbnail',
            render: (file_path: string, record: TImage) => (
                <img
                    src={file_path ? getImagePath(file_path) : 'https://via.placeholder.com/50'}
                    alt="thumbnail"
                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
            ),
        },
        {
            title: t('media.tableHeaders.actions'),
            key: 'actions',
            render: (_: any, record: TImage) => (
                <Space size="middle">
                    {loadingRow === record.id ? (
                        <Spin />
                    ) : editingKey === record.id ? (
                        <>
                            <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
                            <Button icon={<StopOutlined />} onClick={cancel} />
                        </>
                    ) : (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => edit(record)} />
                            <Button icon={<UploadOutlined />} onClick={() => uploadFile(record)} />
                            <Button
                                icon={<DeleteOutlined />}
                                danger
                                onClick={() => handleDelete(record.id)}
                                loading={deleteLoading}
                            />
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={handleAdd}>
                    {t('media.addImage')}
                </Button>
                <Select
                    value={filterCategory}
                    onChange={(val: 'all' | 'project' | 'general') => setFilterCategory(val)}
                    options={[
                        { value: 'all', label: t('media.filters.all') },
                        { value: 'project', label: t('media.categories.project') },
                        { value: 'general', label: t('media.categories.general') },
                    ]}
                />
            </Space>
            <Table
                dataSource={images}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
            />
        </div>
    );
};

export default ImageList;
